import { useState, useEffect, useMemo } from 'react';
import { styled } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Embed as WendEmbed, IEmbedData, IEmbedConfig } from '@wend/embed';
import { mdiChevronRight, mdiCompass } from '@mdi/js';
import Skeleton from '@mui/material/Skeleton';

import { ENV_BASE_URL } from 'env';
import { Icon } from 'common/components/Icon';
import { List } from 'common/components/List';
import {
    ListItem,
    ListItemMedia,
    ListItemSecondaryAction,
    ListItemText,
    ListItemActionText,
} from 'common/components/ListItem';
import { Link } from 'common/components/Link';

import { sanitizeIframeHtml } from './sanitizeIframe';

const joinClassNames = (...names: (string | undefined)[]) =>
    names.filter(Boolean).join(' ');

// True when the html is a bare <iframe>. html whose root is a wrapper element
// (iframely, vimeo) already carries its own responsive box.
const isBareIframe = (html: string) => {
    const { firstElementChild } = new DOMParser().parseFromString(
        html,
        'text/html'
    ).body;

    return firstElementChild?.tagName === 'IFRAME';
};

interface IProps {
    src: string;
    /**
     * Pre-resolved raw embed markup (e.g. a pasted <iframe>). When set,
     * @wend/embed/Iframely resolution is skipped entirely and this is
     * rendered directly (re-sanitized here regardless of how it was
     * validated on insert, since this component also renders stored data).
     */
    html?: string;
    className?: string;
    onError?(): void;
    autoplay?: boolean;
}

const BaseEmbed = ({
    src,
    html: rawHtml,
    className,
    autoplay,
    onError,
}: IProps) => {
    const [translate] = useTranslation();

    const [embedData, setEmbedData] = useState<IEmbedData | undefined>();
    const [errorState, setErrorState] = useState<boolean>(false);

    const sanitizedRawHtml = useMemo(
        () => (rawHtml ? sanitizeIframeHtml(rawHtml) : undefined),
        [rawHtml]
    );

    const rawHtmlElement = useMemo(() => {
        if (!sanitizedRawHtml) return null;

        return (
            <div
                className={joinClassNames(className, 'embed-responsive')}
                dangerouslySetInnerHTML={{ __html: sanitizedRawHtml }}
            />
        );
    }, [className, sanitizedRawHtml]);

    // Memoized so an unrelated re-render (e.g. selection changing elsewhere
    // in the document) doesn't hand React a brand new element for this DOM
    // node — that resets `dangerouslySetInnerHTML`, reloading the iframe.
    const resolvedHtmlElement = useMemo(() => {
        if (!embedData?.html) return null;

        // Bare <iframe> embeds need our 16:9 responsive wrapper. Embeds whose
        // html ships its own wrapper (iframely, vimeo) already size
        // themselves — forcing ours adds black letterbox/pillarbox bars.
        const resolvedClassName =
            embedData.type === 'video' && isBareIframe(embedData.html)
                ? joinClassNames(className, 'embed-responsive')
                : className;

        return (
            <div
                className={resolvedClassName}
                dangerouslySetInnerHTML={{ __html: embedData.html }}
            />
        );
    }, [className, embedData]);

    const embed = useMemo(() => {
        const embedConfig: IEmbedConfig = {
            iframelyConfig: {
                // /iframely is proxied by the backend (:8000 in dev).
                baseUrl: ENV_BASE_URL,
            },
            providerConfig: {
                vimeo: {
                    token: import.meta.env.VITE_VIMEO_TOKEN,
                    autoplay,
                },
                vimeoDirect: {
                    autoplay,
                },
                pluvo: {},
                googleDrive: {
                    apiKey: import.meta.env.VITE_GOOGLE_DRIVE_KEY,
                },
                youtube: {
                    autoplay,
                },
                bunnyStream: {
                    autoplay,
                },
            },
        };

        return new WendEmbed(embedConfig);
    }, [autoplay]);

    useEffect(() => {
        // Pre-resolved content needs no Iframely fetch.
        if (rawHtml) return;

        const handleError = () => {
            setErrorState(true);
            onError && onError();
        };

        // Did cancel will be used to "cancel" setting data when the component is destroyed
        let didCancel = false;

        if (!src) {
            handleError();

            return;
        }

        async function getData() {
            let data;

            try {
                data = await embed.getData(src);
            } catch {
                handleError();

                return;
            }

            if (!didCancel) {
                if (!data) {
                    handleError();

                    return;
                }

                setEmbedData(data);
            }
        }

        getData();

        return () => {
            didCancel = true;
        };
    }, [src, embed, onError, rawHtml]);

    if (rawHtml) {
        if (!rawHtmlElement) return <>{translate('embed.error')}</>;

        return rawHtmlElement;
    }

    if (errorState) {
        return <>{translate('embed.error')}</>;
    }

    if (!embedData) {
        return <Skeleton height={150} variant="rectangular" />;
    }

    const { thumbnailUrl, html, title, description } = embedData;

    if (html && resolvedHtmlElement) return resolvedHtmlElement;

    return (
        <List>
            <ListItem button component={Link} href={src} target="_blank">
                <ListItemMedia color="primary" image={thumbnailUrl}>
                    <Icon path={mdiCompass} />
                </ListItemMedia>
                <ListItemText primary={title} secondary={description} />
                <ListItemActionText>{translate('open')}</ListItemActionText>
                <ListItemSecondaryAction>
                    <Icon path={mdiChevronRight} size="4rem" />
                </ListItemSecondaryAction>
            </ListItem>
        </List>
    );
};

export const Embed = styled(BaseEmbed)`
    min-width: 100%;
    width: 100%;

    iframe {
        width: 100%;
        max-width: 100%;
    }

    &.embed-responsive {
        position: relative;
        display: block;
        overflow: hidden;
        padding: 0;
        pointer-events: auto;
        background-color: ${({ theme }) => theme.vars.palette.common.black};
        min-height: 150px;

        &::before {
            display: block;
            padding-top: 56.25%;
            content: '';
        }

        iframe {
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            height: 100%;
            border: 0;
        }
    }
`;
